import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const IMG = {
  chai: "/images/pexels-photo-14929823.avif", //"https://images.pexels.com/photos/14929823/pexels-photo-14929823.jpeg",
  education: "/images/girl-with-light.jpeg ", //"https://images.unsplash.com/photo-1749700332031-cf99864959ea",
  motivation: "/images/photo-1501743411739-de52ea0ce6a0.jpeg", //"https://images.unsplash.com/photo-1501743411739-de52ea0ce6a0",
  employability: "/images/photo-1522202176988-66273c2fd55f.jpeg", //"https://images.unsplash.com/photo-1522202176988-66273c2fd55f",
  travel: "/images/photo-1476514525535-07fb3b4ae5f1.avif?auto=format&fit=crop&w=1400&q=80", //"https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=1400&q=80",
};

const PILLARS = [
  {
    no: "01",
    title: "Education",
    hindi: "Padhai",
    line: "Gyaan wahi jo sach mein kaam aaye",
    desc: "True education goes beyond the classroom — developing a critical mind and the confidence to navigate a changing world.",
    img: IMG.education,
    span: "md:col-span-7",
    hover: "hover:bg-chai-red hover:text-white",
  },
  {
    no: "02",
    title: "Motivation",
    hindi: "Inspiration",
    line: "Bheed ka hissa nahi, solution ka zariya bano.",
    desc: "Motivation is the spark that gets you started, but consistency finishes the race. Turn every setback into a stepping stone.",
    img: IMG.motivation,
    span: "md:col-span-5",
    hover: "hover:bg-chai-yellow hover:text-chai-ink",
  },
  {
    no: "03",
    title: "Employability",
    hindi: "Kaamyabi",
    line: "Shamta badao, safalta khud raasta dhoond legi",
    desc: "A degree is just the entry ticket. We bridge academic learning and real-world employment to make you truly job-ready.",
    img: IMG.employability,
    span: "md:col-span-12",
    hover: "hover:bg-chai-ink hover:text-white",
  },
  {
    no: "04",
    title: "Travels",
    hindi: "Udaan",
    line: "Travel Simplified. Memories Amplified. Bas Udaan ke saath.",
    desc: "Safar sirf manzil tak pahunchna nahi, balki raaste mein khud ko paana hai. Udaan ke saath travel hota hai simplified — naye sheher, naye nazare, aur naye log.",
    img: IMG.travel,
    span: "md:col-span-12",
    hover: "hover:bg-chai-warmth hover:text-white",
  },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [-6, 6]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative min-h-screen pt-28 md:pt-32 pb-16 overflow-hidden" data-testid="hero">
      <div className="w-full max-w-none px-4 md:px-8 lg:px-12">
        <Reveal delay={0.1}>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-chai-red mb-6">
            A product of chaied.com — since day one
          </p>
        </Reveal>

        <motion.h1 style={{ y: textY }} className="font-heading font-bold uppercase leading-[0.95] tracking-tight text-[13vw] md:text-[8.5vw]">
          {[{ t: "Learning," }, { t: "Growth &" }, { t: "Inspiration.", c: "text-chai-red" }].map((ln, i) => (
            <span key={i} className={`block ${ln.c || ""}`}>
              {ln.t}
            </span>
          ))}
        </motion.h1>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-10 mt-12">
          <div className="max-w-xl">
            <p className="font-serif italic text-3xl md:text-5xl text-chai-ink leading-tight">
              Simplified.
            </p>
            <p className="font-body text-lg text-muted-foreground mt-4">
              Because when learning is as simple as a cup of chai, anything is possible.{" "}
              <span className="text-chai-ink font-semibold">Keep it Simple. Let&apos;s Simplify.</span>
            </p>
            <Link
              to="/services"
              data-testid="hero-cta"
              className="group inline-flex items-center gap-3 bg-chai-ink text-white px-8 py-4 mt-8 font-mono text-xs uppercase tracking-widest hover:bg-chai-red transition-colors"
            >
              Explore The Pillars
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <motion.div
            style={{ y: imgY, rotate: imgRotate }}
            className="relative w-full aspect-[4/5] md:aspect-auto md:h-[500px] md:w-[36%] overflow-hidden border-2 border-chai-ink"
            data-testid="hero-image"
          >
            <img src={IMG.chai} alt="A steaming cup of chai" className="w-full h-full object-cover" />
            <div className="absolute bottom-0 left-0 bg-chai-yellow text-chai-ink font-mono text-xs uppercase tracking-widest px-4 py-2 border-t-2 border-r-2 border-chai-ink">
              Ek cutting chai ☕
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex">
        <ArrowDown className="animate-bounce text-chai-red" />
      </div>
    </section>
  );
}

function Pillars() {
  return (
    <section className="w-full max-w-none px-4 md:px-8 lg:px-12 py-24 md:py-32" data-testid="pillars-section">
      <Reveal className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-14">
        <h2 className="font-heading text-5xl md:text-7xl font-bold tracking-tighter leading-none">
          Our Verticals
        </h2>
        <p className="font-body text-lg text-muted-foreground max-w-sm">
          Empowering minds, shaping futures — one chai at a time.
        </p>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {PILLARS.map((p, i) => (
          <Reveal key={p.no} delay={i * 0.1} className={p.span}>
            <div
              className={`group h-full border border-chai-ink/15 bg-white p-8 md:p-10 transition-colors duration-300 ${p.hover}`}
              data-testid={`pillar-${p.title.toLowerCase()}`}
            >
              <div className="flex items-start justify-between">
                <span className="font-mono text-sm">{p.no}</span>
                <span className="font-mono text-xs uppercase tracking-widest opacity-60">{p.hindi}</span>
              </div>
              <div className="grid md:grid-cols-2 gap-6 mt-6 items-end">
                <div>
                  <h3 className="font-heading text-4xl md:text-6xl font-bold tracking-tight">{p.title}</h3>
                  <p className="font-serif italic text-2xl mt-3">{p.line}</p>
                  <p className="font-body text-base mt-4 opacity-80 max-w-md">{p.desc}</p>
                </div>
                <div className="relative aspect-[4/3] overflow-hidden border border-current/20">
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Promise() {
  const items = ["Saral Bhasha", "Success Stories", "Skill Development", "Positive Mindset"];
  return (
    <section className="bg-chai-ink text-white py-24 md:py-32" data-testid="promise-section">
      <div className="w-full max-w-none px-4 md:px-8 lg:px-12">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-chai-yellow mb-6">Our Promise</p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16">
          {items.map((it, i) => (
            <Reveal key={it} delay={i * 0.08}>
              <div className="flex items-center gap-6 border-b border-white/15 py-8">
                <span className="font-mono text-chai-red text-lg">0{i + 1}</span>
                <span className="font-heading text-3xl md:text-5xl font-semibold tracking-tight">{it}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee items={["Keep it Simple", "Saral Bhasha", "Bheed ka hissa nahi, solution ka zariya bano", "Gyaan wahi jo sach mein kaam aaye"]} />
      <Pillars />
      <Promise />
    </div>
  );
}
